import { ToastSimpleContent } from '@allibee/components/basic';
import { toast, type SvelteToastOptions } from '@zerodevx/svelte-toast';
import { escape } from 'lodash-es';
import { sleep } from '@allibee/utils';

import type { ComponentType } from 'svelte';

export type ToastPosition = 'center' | 'bottom-right';
export type ToastType = 'default' | 'success' | 'fail' | 'spinner' | 'progress';
// 토스트 동작 정책을 클래스 단위로 관리한다.
const TOAST_EXCLUDE_FROM_LIMIT_CLASS = 'toast-exclude-from-limit';
const TOAST_PERSIST_ON_NAV_CLASS = 'toast-persist-on-nav';

export interface ToastDefaultOptions extends Omit<SvelteToastOptions, 'target'> {
    useHtml?: boolean;
    title?: string;
    description?: string;
    descriptionComponent?: ComponentType;
    descriptionProps?: Record<string, unknown>;
    actionText?: string;
    onAction?: () => void;
    onDismiss?: () => void;
    actionComponent?: ComponentType;
    actionProps?: Record<string, unknown>;
    position?: ToastPosition;
    toastType?: ToastType;
    excludeFromLimit?: boolean; // 최대 5개 까지 동시 노출에 제외할건지 (기본값: false)
    persistOnNavigate?: boolean; // 페이지 이동 시 토스트 제거에 제외할건지 (기본값: false)
}
type ToastPresetOptions = Omit<ToastDefaultOptions, 'toastType'>;

/**
 * 토스트 스택 관리 로직
 * excludeFromLimit 옵션이 없는 토스트는 최대 5개 까지 동시 노출
 * 5개 이후부터 가장 1번 토스트는 제거
 */
toast.subscribe(toasts => {
    const countableToasts = toasts.filter(t => !t.classes?.includes(TOAST_EXCLUDE_FROM_LIMIT_CLASS));
    if (countableToasts.length > 5) {
        toast.pop(countableToasts[countableToasts.length - 1].id);
    }
});

/**
 * 페이지 이동 시 persistOnNavigate 옵션이 없는 모든 토스트 제거
 */
export function popNonPersistentToasts() {
    toast.subscribe(toasts => {
        const removableToasts = toasts.filter(t => !t.classes?.includes(TOAST_PERSIST_ON_NAV_CLASS));
        removableToasts.forEach(t => toast.pop(t.id));
    })();
}

/** persistOnNavigate로 표시된 토스트가 현재 떠 있는지 여부 */
export function hasPersistentToasts(): boolean {
    let result = false;
    toast.subscribe(toasts => {
        result = toasts.some(t => t.classes?.includes(TOAST_PERSIST_ON_NAV_CLASS));
    })();
    return result;
}

export async function toastDefault(message: string, options?: ToastDefaultOptions): Promise<number> {
    await sleep(0); // <SvelteToast {options} /> 컴포넌트가 랜더링 되기 전이라 options 적용이 안될 수 있어서 다음 루프로 넘김
    const {
        useHtml,
        title,
        description,
        descriptionComponent,
        descriptionProps,
        actionText,
        onAction,
        onDismiss,
        actionComponent,
        actionProps,
        position,
        toastType = 'default',
        theme,
        classes,
        excludeFromLimit = false,
        persistOnNavigate = false,
        duration,
        ...toastOptions
    } = options ?? {};
    const safeMessage = useHtml ? message : escape(message);
    const safeTitle = title ? (useHtml ? title : escape(title)) : title;
    const safeDescription = description ? (useHtml ? description : escape(description)) : description;
    const defaultDuration = toastType === 'fail' ? 15000 : 5000;
    const resolvedDuration = duration ?? defaultDuration;
    const shouldPersistUntilDismissed = resolvedDuration === 0 && toastOptions.initial === undefined && toastOptions.next === undefined;
    const resolvedClasses = [...(classes ?? []), excludeFromLimit ? TOAST_EXCLUDE_FROM_LIMIT_CLASS : '', persistOnNavigate ? TOAST_PERSIST_ON_NAV_CLASS : ''].filter(Boolean);
    return toast.push(safeMessage, {
        component: {
            src: ToastSimpleContent,
            props: {
                message: safeMessage,
                title: safeTitle,
                description: safeDescription,
                descriptionComponent,
                descriptionProps,
                actionText,
                onAction,
                onDismiss,
                actionComponent,
                actionProps,
                toastType,
            },
            sendIdTo: 'toastId',
        },
        pausable: true,
        dismissable: false,
        duration: resolvedDuration,
        ...(shouldPersistUntilDismissed ? { initial: 1, next: 1 } : {}),
        ...toastOptions,
        classes: resolvedClasses,
        ...(position === 'bottom-right' ? { target: 'bottom-right' } : {}),
        theme: {
            '--toastBarHeight': '0',
            ...(theme ?? {}),
        },
    });
}

export async function toastSuccess(message: string, options?: ToastPresetOptions): Promise<number> {
    return toastDefault(message, {
        ...options,
        toastType: 'success',
        duration: options?.duration ?? 5000,
    });
}

export async function toastDanger(message: string, options?: ToastPresetOptions): Promise<number> {
    return toastDefault(message, {
        ...options,
        toastType: 'fail',
        duration: options?.duration ?? 15000,
    });
}

export async function toastProgress(message: string, options?: ToastPresetOptions): Promise<number> {
    return toastDefault(message, {
        ...options,
        toastType: 'progress',
        duration: options?.duration ?? 5000,
        excludeFromLimit: true,
        persistOnNavigate: true,
    });
}

export async function toastSpinner(message: string, options?: ToastPresetOptions): Promise<number> {
    return toastDefault(message, {
        ...options,
        toastType: 'spinner',
        duration: options?.duration ?? 0,
        excludeFromLimit: true,
        persistOnNavigate: true,
    });
}
